import React, { useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { User, ChevronDown, LogOut, Sun, Moon } from 'lucide-react';
import { BaseComponentProps } from '../types/componentThemes';
import { useTheme } from '../contexts/ThemeContext';

export interface HeaderProps extends BaseComponentProps {
  /** Logo element to display */
  logo?: React.ReactNode;
  /** User name to display in profile menu */
  userName?: string;
  /** User avatar element */
  userAvatar?: React.ReactNode;
  /** Callback when logout is clicked */
  onLogout?: () => void;
  /** Additional content to display in header (e.g., navigation) */
  children?: React.ReactNode;
}

export const Header: React.FC<HeaderProps> = ({
  logo,
  userName,
  userAvatar,
  onLogout,
  children,
  className = '',
  style,
  testId,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { theme, toggleTheme } = useTheme();

  // Track scroll for glassmorphism intensity
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 8);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Close menu on ESC key
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogoutClick = () => {
    setIsMenuOpen(false);
    if (onLogout) {
      onLogout();
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 h-16 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/80 dark:bg-neutral-900/80 backdrop-blur-xl shadow-sm border-b border-neutral-200/50 dark:border-neutral-700/50'
          : 'bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700'
      } ${className}`}
      style={style}
      data-testid={testId}
    >
      <div className="h-full flex items-center justify-between px-6">
        {/* Logo */}
        {logo && (
          <div className="flex-shrink-0" data-testid={testId ? `${testId}-logo` : undefined}>
            {logo}
          </div>
        )}

        {/* Additional content (e.g., navigation) */}
        {children && (
          <div className="flex-1 flex items-center justify-center">
            {children}
          </div>
        )}

        {/* User profile menu */}
        <div className="relative flex-shrink-0" ref={menuRef}>
          <button
            className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 min-w-[44px] min-h-[44px]"
            onClick={toggleMenu}
            aria-label="User menu"
            aria-expanded={isMenuOpen}
            aria-haspopup="true"
            data-testid={testId ? `${testId}-user-menu-button` : undefined}
          >
            {/* User avatar or default icon */}
            {userAvatar ? (
              <div className="w-8 h-8 flex-shrink-0">{userAvatar}</div>
            ) : (
              <div className="w-8 h-8 flex-shrink-0 rounded-full bg-gradient-to-br from-primary-500 to-secondary-600 flex items-center justify-center shadow-sm">
                <User className="w-4 h-4 text-white" />
              </div>
            )}

            {/* User name (hidden on mobile) */}
            {userName && (
              <span className="hidden md:block text-sm font-medium text-neutral-700 dark:text-neutral-200">
                {userName}
              </span>
            )}

            {/* Dropdown arrow */}
            <ChevronDown
              className={`w-4 h-4 text-neutral-400 transition-transform duration-200 ${
                isMenuOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {/* Dropdown menu */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -4 }}
                transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                className="absolute right-0 mt-2 min-w-[12rem] w-max max-w-xs bg-white dark:bg-neutral-800 rounded-xl shadow-lg border border-neutral-200/80 dark:border-neutral-700 py-1 overflow-hidden"
                role="menu"
                aria-orientation="vertical"
                data-testid={testId ? `${testId}-user-menu` : undefined}
              >
                {/* User info */}
                {userName && (
                  <div className="px-4 py-3 border-b border-neutral-100 dark:border-neutral-700">
                    <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{userName}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">Manage your account</p>
                  </div>
                )}

                {/* Dark mode toggle */}
                <button
                  className="w-full text-left px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors duration-150 focus:outline-none focus:bg-neutral-50 dark:focus:bg-neutral-700"
                  onClick={toggleTheme}
                  role="menuitem"
                >
                  <div className="flex items-center gap-3">
                    {theme === 'dark' ? (
                      <Sun className="w-4 h-4 text-amber-500" />
                    ) : (
                      <Moon className="w-4 h-4 text-primary-600" />
                    )}
                    {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                  </div>
                </button>

                {/* Logout button */}
                {onLogout && (
                  <button
                    className="w-full text-left px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors duration-150 focus:outline-none focus:bg-neutral-50 dark:focus:bg-neutral-700"
                    onClick={handleLogoutClick}
                    role="menuitem"
                    data-testid={testId ? `${testId}-logout-button` : undefined}
                  >
                    <div className="flex items-center gap-3">
                      <LogOut className="w-4 h-4 text-neutral-500" />
                      Logout
                    </div>
                  </button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
};
